/**
 * CLI handler: nexus sync:commands
 *
 * Copies distributable Claude Code commands from the installed
 * @gzl10/nexus-backend package to ~/.claude/commands/
 */
import { existsSync, mkdirSync, readdirSync, readFileSync, writeFileSync } from 'node:fs'
import { join, basename } from 'node:path'
import { homedir } from 'node:os'
import { createConsola } from 'consola'
import { getLibPath } from '@kernel/config/paths.js'

const logger = createConsola({ defaults: { tag: 'sync:commands' } })

interface SyncResult {
  added: string[]
  updated: string[]
  unchanged: string[]
}

export async function handleSyncCommands(options: { dryRun?: boolean }): Promise<void> {
  const sourceDir = join(getLibPath(), 'claude-commands')
  const targetDir = join(homedir(), '.claude', 'commands')

  // Verify source exists
  if (!existsSync(sourceDir)) {
    logger.warn(`Commands source not found: ${sourceDir}`)
    logger.info('Make sure @gzl10/nexus-backend is properly installed.')
    return
  }

  // Ensure target directory exists
  if (!existsSync(targetDir)) {
    if (options.dryRun) {
      logger.info(`Would create: ${targetDir}`)
    } else {
      mkdirSync(targetDir, { recursive: true })
      logger.info(`Created: ${targetDir}`)
    }
  }

  // Find all .md files in source
  const sourceFiles = readdirSync(sourceDir).filter(f => f.endsWith('.md'))

  if (sourceFiles.length === 0) {
    logger.warn('No commands found in source directory.')
    return
  }

  const result: SyncResult = { added: [], updated: [], unchanged: [] }

  for (const file of sourceFiles) {
    const sourcePath = join(sourceDir, file)
    const targetPath = join(targetDir, file)
    const sourceContent = readFileSync(sourcePath, 'utf-8')

    if (!existsSync(targetPath)) {
      // New command
      if (!options.dryRun) {
        writeFileSync(targetPath, sourceContent, 'utf-8')
      }
      result.added.push(file)
    } else {
      const targetContent = readFileSync(targetPath, 'utf-8')
      if (sourceContent !== targetContent) {
        // Updated command
        if (!options.dryRun) {
          writeFileSync(targetPath, sourceContent, 'utf-8')
        }
        result.updated.push(file)
      } else {
        result.unchanged.push(file)
      }
    }
  }

  // Report
  const prefix = options.dryRun ? '[dry-run] ' : ''

  if (result.added.length > 0) {
    logger.success(`${prefix}Added: ${result.added.map(f => basename(f, '.md')).join(', ')}`)
  }
  if (result.updated.length > 0) {
    logger.success(`${prefix}Updated: ${result.updated.map(f => basename(f, '.md')).join(', ')}`)
  }
  if (result.unchanged.length > 0) {
    logger.info(`Unchanged: ${result.unchanged.length} commands`)
  }

  const total = result.added.length + result.updated.length
  if (total === 0) {
    logger.info('All commands up to date.')
  } else {
    logger.success(`${prefix}${total} command(s) synced to ${targetDir}`)
    if (options.dryRun) {
      logger.info('Run without --dry-run to apply changes.')
    }
  }
}
